/**
 * Etiquetas de hablante: ÚNICA fuente de verdad para el nombre visible y para
 * la normalización de lo que devuelven los proveedores.
 *
 * - Interna: 'MIC' (micrófono, "Yo") y 'SPEAKER_00', 'SPEAKER_01', … (diarización).
 * - AssemblyAI entrega letras ('A', 'B', …); Deepgram enteros (0, 1, …).
 */

export const MIC_LABEL = 'MIC'

/** Nombre mostrable: el del usuario manda; MIC → Yo; SPEAKER_NN → Participante N+1. */
export function speakerDisplayName(
  etiqueta: string | null | undefined,
  nombre?: string | null
): string {
  if (nombre && nombre.trim()) return nombre.trim()
  if (etiqueta === MIC_LABEL) return 'Yo'
  const n = speakerIndex(etiqueta)
  if (n !== null) return `Participante ${n + 1}`
  return etiqueta ?? 'Desconocido'
}

/** Índice numérico de una etiqueta interna 'SPEAKER_NN', o null. */
export function speakerIndex(etiqueta: string | null | undefined): number | null {
  if (!etiqueta || !etiqueta.startsWith('SPEAKER_')) return null
  const n = parseInt(etiqueta.slice('SPEAKER_'.length), 10)
  return Number.isNaN(n) ? null : n
}

/** Construye la etiqueta interna a partir de un índice. */
export function speakerLabel(index: number): string {
  return `SPEAKER_${String(index).padStart(2, '0')}`
}

/**
 * Normaliza la etiqueta cruda de un proveedor a la etiqueta interna.
 * - 'A'..'Z' (AssemblyAI) → índice alfabético; 'AA' → 26, etc.
 * - '0', '1', 0, 1 (Deepgram) → índice numérico.
 * - 'SPEAKER_03' → se respeta.
 * Cualquier otra cosa → null (el llamador decide el fallback).
 */
export function normalizeProviderSpeaker(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return Number.isInteger(raw) && raw >= 0 ? speakerLabel(raw) : null
  const s = raw.trim()
  if (!s) return null
  if (s === MIC_LABEL) return MIC_LABEL
  if (/^SPEAKER_\d+$/i.test(s)) return speakerLabel(parseInt(s.slice('SPEAKER_'.length), 10))
  if (/^\d+$/.test(s)) return speakerLabel(parseInt(s, 10))
  if (/^[A-Z]+$/i.test(s)) {
    // 'A' → 0, 'Z' → 25, 'AA' → 26 (base 26 bijectiva)
    let n = 0
    for (const ch of s.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64)
    return speakerLabel(n - 1)
  }
  return null
}

/**
 * Renumera etiquetas por orden de primera aparición para que la transcripción
 * final tenga 'SPEAKER_00', 'SPEAKER_01', … sin huecos, y devuelve el mapa
 * etiqueta original → etiqueta final. 'MIC' nunca se renumera.
 */
export function renumberSpeakers(etiquetasEnOrden: string[]): Map<string, string> {
  const map = new Map<string, string>()
  let next = 0
  for (const e of etiquetasEnOrden) {
    if (map.has(e)) continue
    if (e === MIC_LABEL) {
      map.set(e, e)
      continue
    }
    map.set(e, speakerLabel(next++))
  }
  return map
}
